import React, { useState } from 'react';
import { ILocalAddress, IOrderDetailResponse, IShipTo, ISoldTo } from '../../../actions/order-details/get-order-details.action';
import { NO_DATA_FALLBACK } from '../../../configuration';
import { formatAddress } from '../../../utilities/format-address';
import { IOrderDetailsResources } from '../order-details.props.autogenerated';

const isValidLocalAddress = (localAddr?: ILocalAddress): boolean => {
    if (!localAddr) {
        return false;
    }

    for (let i = 1; i <= 9; i++) {
        const val = localAddr[`ShortAddress${i}` as keyof ILocalAddress];
        if (typeof val === 'string' && val.trim() !== '') {
            return true;
        }
    }
    return false;
};

const LocalSwitchToggle: React.FC<{ label: string; CLASS_NAME: string; onClick: () => void }> = ({ label, CLASS_NAME, onClick }) => (
    <a className={`${CLASS_NAME}--local-switch`} onClick={onClick}>
        {label}
    </a>
);

const Address: React.FC<{ address: ISoldTo | IShipTo | ILocalAddress; headline: string }> = ({ address, headline }) => {
    return (
        <address>
            <div className='msc-section-box__body'>
                <h4 className='lxs-create-order__address-information-title'>{headline}</h4>
                {formatAddress(address).map(line => (
                    <p className='py-1' key={line}>
                        {line}
                    </p>
                ))}
            </div>
        </address>
    );
};

const AddressRow: React.FC<{
    soldTo: ISoldTo;
    shipTo: IShipTo;
    hasShipToLocal?: boolean;
    hasSoldToLocal?: boolean;
    resources: IOrderDetailsResources;
}> = ({ soldTo, shipTo, hasShipToLocal = false, hasSoldToLocal = false, resources }) => (
    <div className='row'>
        <div className='col-12 col-sm-6 col-lg-12 col-xl-6'>
            {soldTo && Object.keys(soldTo).length ? (
                hasSoldToLocal ? (
                    <Address address={soldTo.LocalAddress} headline={resources.orderDetailsSoldTo} />
                ) : (
                    <Address address={soldTo} headline={resources.orderDetailsSoldTo} />
                )
            ) : (
                NO_DATA_FALLBACK
            )}
        </div>
        <div className='col-12 col-sm-6 col-lg-12 col-xl-6 mt-lg-3 mt-sm-0 mt-xl-0'>
            {shipTo && Object.keys(shipTo).length ? (
                hasShipToLocal ? (
                    <Address address={shipTo.LocalAddress} headline={resources.orderDetailsShipTo} />
                ) : (
                    <Address address={shipTo} headline={resources.orderDetailsShipTo} />
                )
            ) : (
                NO_DATA_FALLBACK
            )}
        </div>
    </div>
);

const AddressInformation = ({ orderDetails, resources }: { orderDetails: IOrderDetailResponse; resources: IOrderDetailsResources }) => {
    const CLASS_NAME = 'lxs-create-order__address-information';

    const [showGlobalAddress, setShowGlobalAddress] = useState<boolean>(() => {
        const stored = localStorage.getItem(AddressInformation.name);
        return stored !== null ? JSON.parse(stored) : true;
    });

    const { SoldTo, ShipTo } = orderDetails.OrderDetail;

    const hasSoldToLocal = isValidLocalAddress(SoldTo?.LocalAddress);
    const hasShipToLocal = isValidLocalAddress(ShipTo?.LocalAddress);

    const hasAnyLocal = hasSoldToLocal || hasShipToLocal;

    const __handleAddressLocalChange = (componentName: string) => {
        setShowGlobalAddress(prev => {
            const newValue = !prev;
            if (componentName) {
                localStorage.setItem(componentName, JSON.stringify(newValue));
            }
            return newValue;
        });
    };

    return (
        <>
            {showGlobalAddress ? (
                <AddressRow soldTo={SoldTo} shipTo={ShipTo} resources={resources} />
            ) : (
                <AddressRow
                    soldTo={SoldTo}
                    shipTo={ShipTo}
                    hasShipToLocal={hasShipToLocal}
                    hasSoldToLocal={hasSoldToLocal}
                    resources={resources}
                />
            )}
            {hasAnyLocal && (
                <LocalSwitchToggle
                    label={
                        showGlobalAddress
                            ? resources.orderDetailsAddressInformationLocalSwitchButton
                            : resources.orderDetailsAddressInformationGlobalSwitchButton
                    }
                    CLASS_NAME={CLASS_NAME}
                    onClick={() => __handleAddressLocalChange(AddressInformation.name)}
                />
            )}
        </>
    );
};

export default AddressInformation;
